import { useState } from "react";
import { X, CalendarDays, Clock, Building2, Quote, CheckCircle2, ArrowRight, MessageCircle, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { CONTACT } from "../lib/content";

/**
 * Modal de detalle de caso de portafolio.
 * Para agregar un nuevo caso, solo agrega una entrada en PORTFOLIO (content.js)
 * con las mismas llaves (slug, title, tag, challenge, solution, results, stack, etc.).
 */
export default function CaseStudyDialog({ caseData, open, onOpenChange }) {
  const [imgIdx, setImgIdx] = useState(0);

  if (!caseData) return null;
  const gallery = caseData.gallery && caseData.gallery.length > 0 ? caseData.gallery : [caseData.image];

  const prev = () => setImgIdx((i) => (i - 1 + gallery.length) % gallery.length);
  const next = () => setImgIdx((i) => (i + 1) % gallery.length);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`case-study-dialog-${caseData.slug}`}
        className="max-w-5xl max-h-[92vh] overflow-hidden p-0 border-0 bg-slate-900 text-white gap-0"
      >
        <DialogTitle className="sr-only">{caseData.title}</DialogTitle>
        <DialogDescription className="sr-only">{caseData.description}</DialogDescription>

        <div className="grid md:grid-cols-5 h-full max-h-[92vh] overflow-hidden">
          {/* Galería */}
          <div className="relative md:col-span-2 bg-slate-950 md:max-h-[92vh] max-h-[50vh] min-h-[280px]">
            <img
              key={imgIdx}
              src={gallery[imgIdx]}
              alt={caseData.title}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-slate-950/20 md:bg-gradient-to-r md:from-transparent md:to-slate-900" />

            {gallery.length > 1 && (
              <>
                <button
                  onClick={prev}
                  data-testid="case-gallery-prev"
                  aria-label="Imagen anterior"
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 backdrop-blur border border-white/10 text-white transition-colors"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={next}
                  data-testid="case-gallery-next"
                  aria-label="Imagen siguiente"
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 backdrop-blur border border-white/10 text-white transition-colors"
                >
                  <ChevronRight size={18} />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {gallery.map((_, i) => (
                    <span
                      key={i}
                      className={`h-1.5 rounded-full transition-all ${i === imgIdx ? "w-8 bg-orange-500" : "w-2 bg-white/40"}`}
                    />
                  ))}
                </div>
              </>
            )}

            <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-widest bg-orange-500 text-white rounded-full px-3 py-1 shadow">
              {caseData.tag}
            </span>
          </div>

          {/* Contenido */}
          <div className="md:col-span-3 overflow-y-auto md:max-h-[92vh] max-h-[70vh] p-6 md:p-10">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight text-white leading-tight">
                  {caseData.title}
                </h2>
                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-xs font-mono text-slate-400 uppercase tracking-widest">
                  {caseData.year && (
                    <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} />{caseData.year}</span>
                  )}
                  {caseData.duration && (
                    <span className="inline-flex items-center gap-1.5"><Clock size={13} />{caseData.duration}</span>
                  )}
                  {caseData.industry && (
                    <span className="inline-flex items-center gap-1.5"><Building2 size={13} />{caseData.industry}</span>
                  )}
                </div>
              </div>
              <button
                onClick={() => onOpenChange(false)}
                data-testid="case-study-close"
                aria-label="Cerrar"
                className="shrink-0 h-9 w-9 flex items-center justify-center rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-slate-300 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Métricas destacadas */}
            {caseData.metrics && caseData.metrics.length > 0 && (
              <div className="grid grid-cols-2 gap-3 my-6">
                {caseData.metrics.map((m) => (
                  <div
                    key={m}
                    className="bg-gradient-to-br from-purple-700/20 to-orange-500/10 border border-purple-500/20 rounded-xl p-4"
                  >
                    <div className="font-display font-extrabold text-xl md:text-2xl text-white">{m}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Challenge */}
            {caseData.challenge && (
              <section className="mt-6">
                <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-orange-400 mb-2">
                  <span>{`{ `}</span>el desafío<span>{` }`}</span>
                </h3>
                <p className="text-slate-300 leading-relaxed">{caseData.challenge}</p>
              </section>
            )}

            {/* Solution */}
            {caseData.solution && (
              <section className="mt-6">
                <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-orange-400 mb-2">
                  <span>{`{ `}</span>nuestra solución<span>{` }`}</span>
                </h3>
                <p className="text-slate-300 leading-relaxed">{caseData.solution}</p>
              </section>
            )}

            {/* Results */}
            {caseData.results && caseData.results.length > 0 && (
              <section className="mt-6">
                <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-orange-400 mb-3">
                  <span>{`{ `}</span>resultados<span>{` }`}</span>
                </h3>
                <ul className="space-y-2.5">
                  {caseData.results.map((r) => (
                    <li key={r} className="flex items-start gap-3 text-slate-300">
                      <CheckCircle2 className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />
                      <span className="leading-relaxed">{r}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Stack */}
            {caseData.stack && caseData.stack.length > 0 && (
              <section className="mt-6">
                <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-orange-400 mb-3">
                  <span>{`{ `}</span>stack<span>{` }`}</span>
                </h3>
                <div className="flex flex-wrap gap-2">
                  {caseData.stack.map((t) => (
                    <span
                      key={t}
                      className="font-mono text-xs text-slate-200 bg-slate-800/80 border border-white/10 rounded-full px-3 py-1.5"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* Testimonial */}
            {caseData.testimonial && (
              <section className="mt-8 border-l-2 border-orange-500/60 pl-5">
                <Quote className="h-5 w-5 text-orange-500 mb-2" />
                <blockquote className="italic text-slate-200 text-base md:text-lg leading-relaxed">
                  "{caseData.testimonial.quote}"
                </blockquote>
                <figcaption className="mt-3 text-sm text-slate-400 font-mono">
                  — {caseData.testimonial.author} · {caseData.testimonial.role}
                </figcaption>
              </section>
            )}

            {/* CTA footer */}
            <footer className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                data-testid="case-study-cta-contact"
                className="bg-gradient-to-r from-purple-700 to-orange-600 hover:from-purple-600 hover:to-orange-500 text-white font-semibold rounded-full flex-1 h-11"
              >
                <a
                  href="#contacto"
                  onClick={() => onOpenChange(false)}
                >
                  Quiero algo así <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button
                asChild
                data-testid="case-study-cta-whatsapp"
                variant="outline"
                className="border-white/20 bg-white/5 hover:bg-white/10 text-white rounded-full h-11"
              >
                <a href={CONTACT.whatsappUrl} target="_blank" rel="noreferrer">
                  <MessageCircle className="mr-2 h-4 w-4 text-green-400" />
                  WhatsApp
                </a>
              </Button>
            </footer>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
